'use client';

import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import clsx from 'clsx';
import { FC, KeyboardEvent, SubmitEvent, useEffect, useRef, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';

import { Button } from '@/general/components/Button/Button';
import { KeywordSearchResults } from '@/general/components/KeywordSearch/KeywordSearchResults';
import { useKeywordSearchSections } from '@/general/components/KeywordSearch/useKeywordSearchSections';
import { getSearchDestination, isNaturalLanguageTerm } from '@/general/helpers/searchDestination.helper';
import { useRouter } from '@/general/helpers/useRouter';
import { isEmpty } from '@/utils/isEmpty';
import { pushToDataLayer } from '@/utils/pushToDataLayer';

import { FilterParams } from '../PlpFilters/PlpFilters.type';
import { getKeywordFromFilterParam } from './PlpSearchBar.helper';

interface Props {
  plpParams?: FilterParams;
  llmQuery?: string;
  isSemanticSearchEnabled?: boolean;
}

export const PlpSearchBar: FC<Props> = ({ plpParams, llmQuery, isSemanticSearchEnabled = false }) => {
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const routeTerm = llmQuery ?? getKeywordFromFilterParam(plpParams?.filter);
  const [term, setTerm] = useState<string>(routeTerm);
  const [renderedRouteTerm, setRenderedRouteTerm] = useState<string>(routeTerm);
  const [open, setOpen] = useState(false);

  if (routeTerm !== renderedRouteTerm) {
    setRenderedRouteTerm(routeTerm);
    setTerm(routeTerm);
    setOpen(false);
  }

  const [debouncedTerm] = useDebounceValue(term, 200);

  const keywordMode = !isNaturalLanguageTerm(term);
  const { sections } = useKeywordSearchSections(debouncedTerm, 'inline', open && keywordMode);

  const hasPanelContent = isEmpty(term.trim()) || (sections?.length ?? 0) > 0;
  const showDropdown = open && keywordMode && hasPanelContent;

  const { refs, floatingStyles, context } = useFloating({
    whileElementsMounted: autoUpdate,
    open: showDropdown,
    onOpenChange: setOpen,
    placement: 'bottom-start',
    middleware: [
      offset(8),
      flip({ padding: 16 }),
      size({
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${Math.max(availableHeight - 16, 200)}px`,
          });
        },
        padding: 16,
      }),
    ],
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useDismiss(context, { escapeKey: false }),
    useRole(context, { role: 'listbox' }),
  ]);

  useEffect(() => {
    if (debouncedTerm !== '') {
      pushToDataLayer({
        event: 'search_attempt',
        value: debouncedTerm,
      });
    }
  }, [debouncedTerm]);

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    setOpen(false);

    router.push(getSearchDestination(term));
  };

  const moveFocusInPanel = (delta: number) => {
    const options = Array.from(
      refs.floating.current?.querySelectorAll<HTMLElement>('button:not([disabled]), a[href]') ?? [],
    );
    if (options.length === 0) {
      return;
    }
    const activeOption = document.activeElement;
    const currentIndex = activeOption instanceof HTMLElement ? options.indexOf(activeOption) : -1;
    const firstIndex = delta > 0 ? 0 : options.length - 1;
    const nextIndex = currentIndex === -1 ? firstIndex : (currentIndex + delta + options.length) % options.length;
    options[nextIndex]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      inputRef.current?.focus();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      requestAnimationFrame(() => moveFocusInPanel(1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveFocusInPanel(-1);
    }
  };

  return (
    <div className="relative mb-2" data-testid="plp-search">
      <form
        ref={refs.setReference}
        onSubmit={handleSubmit}
        role="search"
        className={clsx(
          'relative flex h-14 items-center rounded-lg border bg-for-neu-tie1 transition-colors focus-within:border-for-pri-bas',
          showDropdown ? 'border-for-pri-bas' : 'border-bor-neu-mut',
        )}
      >
        <input
          ref={inputRef}
          type="text"
          name="search"
          value={term}
          autoComplete="off"
          aria-autocomplete="list"
          aria-label="Search cars"
          className="c-fs-body1 h-full w-full bg-transparent pr-23 pl-3 text-tex-def-tie1 placeholder:text-tex-def-tie2 focus:outline-none"
          data-testid="plp-search-input"
          placeholder="Search makes, models, body..."
          // `useRole` supplies role/aria-expanded/aria-controls; handlers merge through here.
          {...getReferenceProps({
            onChange: (event) => setTerm((event.target as HTMLInputElement).value),
            onFocus: () => setOpen(true),
            onClick: () => setOpen(true),
            onKeyDown: handleKeyDown,
          })}
        />
        <div className="absolute right-2">
          <Button type="submit" variant="accent" size="small" data-testid="plp-search-submit">
            Search
          </Button>
        </div>
      </form>

      {showDropdown && (
        <FloatingPortal>
          <FloatingFocusManager
            context={context}
            initialFocus={-1}
            returnFocus={false}
            visuallyHiddenDismiss
            modal={false}
          >
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              className="z-30 overflow-y-auto rounded-lg border border-bor-neu-mut bg-for-neu-tie1 shadow-elevation-24dp"
              data-testid="plp-search-dropdown"
              {...getFloatingProps({ onKeyDown: handleKeyDown })}
            >
              <KeywordSearchResults
                searchTerm={debouncedTerm}
                variant="inline"
                onResultClick={() => setOpen(false)}
                isSemanticSearchEnabled={isSemanticSearchEnabled}
              />
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </div>
  );
};
